import { useInfiniteQuery } from '@tanstack/vue-query'
import { date as qdate } from 'quasar'
import { computed, Ref, ref, watch } from 'vue'
import { ComposerTranslation } from 'vue-i18n'

import axios from '@/plugins/axios'
import { RequestData, WrappedRequest } from '@/types/curRequest'

import { queryKeyToUrl, ReApi, ReApiVersion, ReEndpoint } from './query'
import wrapRequest from './requestWrapper'
import { useCurUser } from './useCurUser'
import { reactiveAny } from './utils/reactive'

// Helper to generate time filter options for select components
export const getTimeFilterOptions = (t: ComposerTranslation) => [
  { label: t('monolith.dashboard.last_month'), value: 'last_month', days: 30 },
  { label: t('monolith.dashboard.last_week'), value: 'last_week', days: 7 },
]

// Types for date filter objects
export interface DateFilter {
  type: 'date'
  from?: string | Date | null
  to?: string | Date | null
}

type FilterValue = string | number | boolean | DateFilter | Array<unknown> | null | undefined

interface ColFilters {
  [key: string]: FilterValue
}

interface ServerSideFilters {
  [key: string]: FilterValue
}

interface Pagination {
  sortBy?: string
  descending?: boolean
  page?: number
  rowsPerPage?: number
}

// Pagination info from x-pagination header
interface PaginationHeader {
  next_at?: string
}

// Page param structure for infinite query - uses next_at from server
interface PageParam {
  modified_at__to?: string | null // End date for next page (from server's next_at)
}

// Query page response
interface PageResponse {
  data: RequestData[]
  pagination: PaginationHeader | null
}

// Filter function type
type FilterFunction = (item: WrappedRequest) => boolean

// Options for useRequestList
interface UseRequestListOptions {
  isUserRequests?: boolean
  serverSideFilters?: ServerSideFilters
  filters?: FilterFunction[]
  colFilters?: ColFilters
  pagination?: Pagination
  enableDatePagination?: boolean
  enabled?: boolean
  initDaysToLoad?: number
  limit?: Ref<number>
  quickFilter?: Ref<string | null>
}

/**
 * Enhanced requestList composable with server-side filtering support
 *
 * When colFilters are provided, they are automatically included in API calls
 * as query parameters (e.g., ?customer_ref=TEST&status=active).
 *
 * Pagination is now driven by the x-pagination header from the server,
 * which provides has_more and date boundaries for the next page.
 */
export function useRequestList({
  isUserRequests: initIsUserRequests,
  serverSideFilters: initServerSideFilters,
  filters: initFilters,
  colFilters: initColFilters,
  pagination: initPagination,
  enableDatePagination: initEnableDatePagination = false,
  enabled: initEnabled = true,
  initDaysToLoad = 7,
  limit = ref(500),
  quickFilter: initQuickFilter,
}: UseRequestListOptions = {}) {
  const curUser = useCurUser()

  const isUserRequests = ref<boolean>(initIsUserRequests ?? false)
  const valReqsFilter = ref<string | null>(null)
  const customerRefFilter = ref<string | null>(null)
  const colFilters = ref<ColFilters>(initColFilters ?? {})
  const quickFilter = initQuickFilter ?? ref<string | null>(null)
  const serverSideFilters = ref<ServerSideFilters>(initServerSideFilters ?? {})
  const pagination = ref<Pagination | undefined>(initPagination)
  const enabled = ref(initEnabled)

  const enableDatePagination = ref<boolean>(initEnableDatePagination)

  const SORTABLE_FIELDS = [
    'borrower',
    'building_type',
    'customer_ref',
    'last_submission_lang',
    'status',
    'owner',
    'customer',
    'valuation_request_ref',
    'valuation_type',
    'valuer',
    'created_at',
    'modified_at',
    'last_cycle_start_at',
    'latest_appointment',
  ]

  // {Array<Function>} - An array of functions, where each function represents a condition.
  const filters = ref<FilterFunction[]>(initFilters ?? [])

  // {Function} - Function that represents a condition (only allow one at a time).
  const focusFilter = ref<FilterFunction | null>(null)

  const daysToLoad = computed<number>(() => {
    if (curUser.is.value.onlyBorrower) {
      return 365 // 1 year
    }

    return initDaysToLoad // 1 week
  })

  const isUsingDateFilters = computed<boolean>(() => {
    const _filters: ColFilters = Object.assign({}, serverSideFilters.value, colFilters.value)
    return !!Object.entries(_filters).find(([_, value]) => {
      return (
        value &&
        typeof value === 'object' &&
        (value as DateFilter).type === 'date' &&
        ((value as DateFilter).from || (value as DateFilter).to)
      )
    })
  })

  /**
   * Calculate the initial start date based on default days to load
   */
  function getInitialStartDate(): string {
    const today = new Date()
    return new Date(today.setDate(today.getDate() - daysToLoad.value)).toISOString()
  }

  const requestQueryParams = computed<Record<string, unknown> | null>(() => {
    const params: Record<string, unknown> = { limit: limit.value }

    // Add quick_filter if set
    if (quickFilter.value) {
      params.quick_filter = quickFilter.value
    }

    if (valReqsFilter.value) {
      params.valuation_request_refs = valReqsFilter.value
    } else if (customerRefFilter.value) {
      params.customer_ref = customerRefFilter.value
    }

    // Add filters as server-side parameters
    const _filters: ColFilters = { ...serverSideFilters.value, ...colFilters.value } // make sure colFilters override
    if (_filters && Object.keys(_filters).length > 0) {
      params.limit = limit.value

      Object.entries(_filters).forEach(([col, value]) => {
        if (!value) return

        // explicitly setting null is important to clear previous filters
        if (typeof value === 'object' && (value as DateFilter).type === 'date') {
          const dateFilter = value as DateFilter
          // Use Quasar's formatDate to get local date string without timezone conversion
          const toDateStr = (d: string | Date | null | undefined) =>
            d ? qdate.formatDate(d, 'YYYY-MM-DD') : null
          const fromStr = toDateStr(dateFilter.from)
          const toStr = toDateStr(dateFilter.to)

          params[`${col}__from`] = fromStr ? `${fromStr}T00:00:00.000Z` : null
          params[`${col}__to`] = toStr ? `${toStr}T23:59:59.999Z` : null
          return
        }

        const filter =
          typeof value === 'string' ? value.trim() : (value as string | number | boolean).toString()
        if (filter && filter !== '') {
          params[col] = filter
        }
      })
    }

    // if SORTABLE_FIELDS includes pagination.sortBy, add order_by param (table handles the rest of sorting)
    if (pagination.value?.sortBy && SORTABLE_FIELDS.includes(pagination.value.sortBy)) {
      params.order_by = `${pagination.value?.sortBy}${pagination.value.descending ? '__desc' : '__asc'}`
    }

    return Object.keys(params).length > 0 ? params : null
  })

  const queryKey = computed(() => {
    const params = requestQueryParams.value ?? {}

    return isUserRequests.value
      ? [ReApi.VALUATION, ReApiVersion.CURRENT, ReEndpoint.USER, 'request', params]
      : [ReApi.VALUATION, ReApiVersion.CURRENT, ReEndpoint.REQUEST, params]
  })

  /**
   * Parse the x-pagination header from the response
   */
  function parsePaginationHeader(headers: Record<string, string>): PaginationHeader | null {
    const paginationHeader = headers['x-pagination']
    if (!paginationHeader) return null

    try {
      return JSON.parse(paginationHeader) as PaginationHeader
    } catch {
      console.warn('Failed to parse x-pagination header:', paginationHeader)
      return null
    }
  }

  const requestQuery = useInfiniteQuery({
    queryKey,
    queryFn: async ({ pageParam, queryKey: key }): Promise<PageResponse> => {
      // Build params from queryKey
      const params = { ...((key[key.length - 1] as Record<string, unknown>) || {}) }

      // Add date range params for pagination only if:
      // 1. Date pagination is enabled
      // 2. No custom date filters are being used
      if (enableDatePagination.value && !isUsingDateFilters.value) {
        // For first page, use initial date range; for subsequent pages, use server-provided boundary
        if (!pageParam?.modified_at__to) {
          params.modified_at__from = getInitialStartDate()
          params.modified_at__to = new Date().toISOString()
        } else {
          // Use the previous page's start date as this page's end date
          // Calculate new start date by going back the same number of days
          const endDate = new Date(pageParam.modified_at__to)
          const startDate = new Date(endDate)
          startDate.setDate(startDate.getDate() - daysToLoad.value)

          params.modified_at__from = startDate.toISOString()
          params.modified_at__to = pageParam.modified_at__to
        }
      }

      // Build URL from queryKey (without the params object)
      const keyWithoutParams = key.slice(0, -1).filter((k) => k !== null)
      const { url } = queryKeyToUrl(keyWithoutParams)

      const response = await axios.get<RequestData[]>(url, { params })
      const pagination = parsePaginationHeader(response.headers as Record<string, string>)

      return { data: response.data, pagination }
    },
    initialPageParam: {} as PageParam,
    getNextPageParam: (lastPage: PageResponse): PageParam | undefined => {
      // If date pagination is disabled, never fetch more pages
      if (!enableDatePagination.value) {
        return undefined
      }

      const { pagination } = lastPage

      // Use server's next_at flag to determine if there are more pages
      if (!pagination?.next_at) {
        return undefined
      }

      // Use the server's next_at as the end boundary for the next page
      // This ensures the next page will have results since the backend calculated the optimal boundary
      return { modified_at__to: pagination.next_at }
    },
    select: (queryData): WrappedRequest[] => {
      // Flatten all pages and wrap requests
      return (
        queryData.pages
          .flatMap((page) => page.data)
          .map(wrapRequest)
          // @ts-expect-error not sure where this .error comes from
          .filter((request: WrappedRequest) => !request.error)
      )
    },
    placeholderData: (previousData) => previousData, // show previous data while loading new data (!)
    staleTime: 60000, // 1 minute
    refetchOnWindowFocus: false,
    enabled: enabled,
  })

  const onLoad = function (callback: (arg0: WrappedRequest[] | undefined) => void) {
    if (requestQuery.isFetched.value) {
      callback(requestQuery.data.value)
    } else {
      const unwatch = watch(requestQuery.isFetched, function () {
        if (requestQuery.isFetched.value) {
          callback(requestQuery.data.value)
          unwatch()
        }
      })
    }
  }

  const data = computed<WrappedRequest[]>(() => (requestQuery.data?.value as WrappedRequest[]) ?? [])

  /**
   * If `data.value` is empty or undefined, it returns an empty array.
   * If no filters or mutual exclusive filter are provided, it returns the unfiltered data.
   * If a mutual exclusive filter is provided and is a function, it is combined with the existing filters.
   * The data is filtered such that every item satisfies all the provided filters.
   */
  const getFilteredData = computed<WrappedRequest[]>(() => {
    if (!filters.value.length && !focusFilter.value) {
      return data.value
    }

    const _filters: FilterFunction[] =
      focusFilter.value && typeof focusFilter.value === 'function'
        ? filters.value.concat(focusFilter.value)
        : filters.value

    return data.value.filter((item) => _filters.every((filter) => filter(item)))
  })

  /**
   * Calculates the count of items in `data.value` that satisfy all the provided conditions.
   */
  function getCount(conditions: FilterFunction[] = []): number {
    if (!conditions.length) {
      return data.value.length
    }

    const filteredConditions = conditions.concat(filters.value).filter(Boolean)
    return data.value.filter((item) => filteredConditions.every((filter) => filter(item))).length
  }

  function setFocusFilter(filter: FilterFunction | null): void {
    focusFilter.value = filter
  }

  function setColFilters(newFilters: ColFilters): void {
    colFilters.value = newFilters
  }

  function setPagination(value: Pagination): void {
    pagination.value = value
  }

  function loadMoreDays(): void {
    if (
      requestQuery.hasNextPage.value &&
      !requestQuery.isFetchingNextPage.value &&
      !isUsingDateFilters.value
    ) {
      requestQuery.fetchNextPage()
    }
  }

  function setQuickFilter(value: string | null): void {
    quickFilter.value = value
  }

  const isBusy = reactiveAny(requestQuery.isLoading, requestQuery.isFetching, requestQuery.isFetchingNextPage)

  // TODO - this should come from a separate /user/projects endpoint and moved out
  const projects = computed(() => {
    return (
      requestQuery.data.value?.filter((req) => Array.isArray(req.projects) && req.projects.length > 0) ?? []
    )
  })

  return {
    serverSideFilters,
    valReqsFilter,
    customerRefFilter,
    colFilters,
    quickFilter,
    isUserRequests,
    queryKey,
    onLoad,
    projects,
    isLoaded: computed(() => requestQuery.isFetched.value && !requestQuery.isError.value),
    ...requestQuery,
    data, // Override requestQuery.data with our computed version
    isBusy,
    hasNextPage: requestQuery.hasNextPage,
    isFetchingNextPage: requestQuery.isFetchingNextPage,
    getCount,
    getFilteredData,
    setFocusFilter,
    setColFilters,
    setPagination,
    setQuickFilter,
    loadMoreDays,
    isUsingDateFilters,
    enabled,
  }
}
